import React, { useEffect, useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2, Link as LinkIcon, Star } from "lucide-react";
import imageCompression from "browser-image-compression";
import { BACKEND_URL, getAuthToken } from "../lib/api";
import { toast } from "sonner";

/**
 * Resolve stored URLs that may be relative (/api/files/...) against the backend.
 */
export function resolveImageUrl(url) {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
    return `${BACKEND_URL}${url}`;
}

/**
 * Return a sized variant URL for a stored full-size image URL.
 *   variantUrl("/api/files/.../abc.jpg", "thumb") → "/api/files/.../abc-thumb.jpg"
 *   variantUrl("/api/files/.../abc.jpg", "card")  → "/api/files/.../abc-card.jpg"
 *   variantUrl("",  ...)                          → ""
 *   variantUrl("/api/files/.../abc.gif", "thumb") → unchanged (we don't render variants for GIF)
 *
 * Variants are JPEG only (see backend IMAGE_VARIANTS). The frontend assumes
 * the variant exists when the source is a JPG/PNG/WEBP > the target edge —
 * if R2 returns 404 the proxy falls back to the original `url`.
 */
export function variantUrl(url, label) {
    if (!url || !label) return resolveImageUrl(url);
    // Don't try to grab a variant of a GIF — we skip those server-side.
    const lower = url.toLowerCase();
    if (lower.endsWith(".gif")) return resolveImageUrl(url);
    // Replace the last extension with `-<label>.jpg`.
    const i = url.lastIndexOf(".");
    if (i < 0) return resolveImageUrl(url);
    return resolveImageUrl(`${url.slice(0, i)}-${label}.jpg`);
}

// ---------- Client-side resize/optimise -------------------------------
// Phones shoot 12-MP photos (~4-6MB each) that we display at 800-1200px wide.
// Resizing to a 2400px long-edge JPEG before upload cuts file size 5-10×
// and upload time proportionally. Runs entirely in the browser via canvas.
//
// Skipped for tiny files (< 600KB — already optimised) and for the
// already-targeted formats `image/gif` (animation loss) and unknown mimes.
// HEIC is auto-converted to JPEG by the canvas pipeline — bonus iOS fix.
const COMPRESS_OPTS = {
    maxSizeMB: 2,           // hard cap; usually we land well under this
    maxWidthOrHeight: 2400, // long-edge cap
    initialQuality: 0.85,
    useWebWorker: true,
    fileType: "image/jpeg",
};

async function maybeCompress(file) {
    if (!file) return file;
    if (file.size < 600 * 1024) {
        console.log("[upload] skipping compression (< 600KB):", file.name, file.size);
        return file;
    }
    if (file.type === "image/gif") return file;      // would lose animation
    if (!file.type.startsWith("image/")) return file;
    console.log("[upload] compressing:", file.name, file.size);
    try {
        const compressed = await imageCompression(file, COMPRESS_OPTS);
        console.log(
            `[upload] compressed ${file.name}: ${(file.size / 1024).toFixed(0)}KB → ${(compressed.size / 1024).toFixed(0)}KB`,
        );
        // Preserve the original filename so toasts/logs read naturally.
        return new File(
            [compressed],
            file.name.replace(/\.(heic|heif)$/i, ".jpg"),
            { type: compressed.type || "image/jpeg" },
        );
    } catch (err) {
        // Compression isn't critical — fall back to the original on failure.
        console.warn(`[upload] compression failed for ${file.name}, uploading original:`, err);
        return file;
    }
}

/**
 * Raw XHR upload helper — bypasses axios entirely so the browser handles
 * `multipart/form-data; boundary=...` natively. We use XMLHttpRequest
 * (rather than fetch) so we get `xhr.upload.onprogress` for free; this
 * lets the gallery overlay a real-time percentage on each pending
 * thumbnail while the bytes are travelling.
 *
 * Returns { url, ...rest } on 2xx, throws an Error with the backend detail
 * (or HTTP status) on anything else.
 */
function uploadImageRaw(file, onProgress) {
    return new Promise((resolve, reject) => {
        console.log("[upload] uploadImageRaw start", { name: file.name, size: file.size, type: file.type });
        const fd = new FormData();
        fd.append("file", file);
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${BACKEND_URL}/api/uploads/image`, true);
        xhr.withCredentials = true;
        // Hard timeout — if R2 stalls or the connection drops mid-flight,
        // we want to fail loudly instead of leaving the user staring at "0%".
        xhr.timeout = 60_000;
        const token = getAuthToken();
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        try {
            const code = window.localStorage.getItem("rwo_beta_code") || "";
            if (code) xhr.setRequestHeader("X-Beta-Access", code);
        } catch { /* noop */ }
        // No Content-Type header — XHR will set it to
        // `multipart/form-data; boundary=...` when we hand it a FormData body.
        if (typeof onProgress === "function") {
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            };
        }
        xhr.onload = () => {
            let data = null;
            try { data = JSON.parse(xhr.responseText || "null"); } catch { /* may be empty */ }
            if (xhr.status >= 200 && xhr.status < 300) {
                if (typeof onProgress === "function") onProgress(100);
                resolve(data);
            } else {
                const detail = data?.detail || `${xhr.status} ${xhr.statusText}`;
                reject(new Error(typeof detail === "string" ? detail : JSON.stringify(detail)));
            }
        };
        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.onabort = () => reject(new Error("Upload aborted"));
        xhr.ontimeout = () => reject(new Error("Upload timed out after 60s"));
        xhr.send(fd);
    });
}

/**
 * Flexible image uploader.
 *
 * Props:
 *  - value: current URL (may be "/api/files/..." or full http(s) URL) or "".
 *  - onChange(url): called with the new URL when uploaded / selected / removed.
 *  - samples?: array of sample URLs to offer as a grid (optional).
 *  - aspect: "aspect-square" | "aspect-[21/9]" | custom — preview box shape.
 *  - testid: prefix for data-testids.
 *  - allowPaste: bool — show "Paste URL" toggle.
 *  - fallback: shown inside preview box when no image present.
 */
export default function ImageUploader({
    value,
    onChange,
    samples,
    aspect = "aspect-square",
    testid = "image",
    allowPaste = true,
    fallback = null,
}) {
    const [uploading, setUploading] = useState(false);
    const [paste, setPaste] = useState(false);
    const inputRef = useRef(null);
    const preview = resolveImageUrl(value);

    const onFile = async (file) => {
        if (!file) return;
        if (!/^image\//.test(file.type)) {
            toast.error("Please select an image file.");
            return;
        }
        if (file.size > 25 * 1024 * 1024) {
            toast.error(`Image too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max 25MB.`);
            return;
        }
        setUploading(true);
        try {
            const optimised = await maybeCompress(file);
            const data = await uploadImageRaw(optimised);
            console.log("[upload]", file.name, "→", data);
            if (!data?.url) throw new Error("Server returned no URL");
            onChange(data.url);
            toast.success("Uploaded");
        } catch (err) {
            console.error("Image upload failed:", err);
            toast.error(err?.message || "Upload failed", { duration: 10000 });
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-start gap-3">
                <div
                    className={`w-24 ${aspect === "aspect-square" ? "h-24" : "h-16"} rounded-md overflow-hidden bg-trail-tan flex items-center justify-center text-midnight-pine font-display font-black text-xl shrink-0 border border-ridge-line`}
                >
                    {preview ? (
                        <img
                            src={preview}
                            alt=""
                            className="w-full h-full object-cover"
                            data-testid={`${testid}-current`}
                        />
                    ) : (
                        fallback ?? <ImageIcon className="w-6 h-6 text-slate-gray" />
                    )}
                </div>

                <div className="flex flex-wrap gap-1.5 flex-1">
                    <button
                        type="button"
                        data-testid={`${testid}-upload-btn`}
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-midnight-pine text-white hover:bg-midnight-pine/90 active:scale-95 disabled:opacity-60 transition-all"
                    >
                        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {uploading ? "Uploading…" : value ? "Replace" : "Upload"}
                    </button>
                    {value && (
                        <button
                            type="button"
                            data-testid={`${testid}-remove-btn`}
                            onClick={() => onChange("")}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line text-granite hover:bg-alpine-mist"
                        >
                            <X className="w-3 h-3" /> Remove
                        </button>
                    )}
                    {allowPaste && (
                        <button
                            type="button"
                            data-testid={`${testid}-paste-toggle`}
                            onClick={() => setPaste((p) => !p)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line text-granite hover:bg-alpine-mist"
                        >
                            <LinkIcon className="w-3 h-3" /> {paste ? "Hide URL" : "Paste URL"}
                        </button>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        data-testid={`${testid}-file-input`}
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = ""; // allow same-file re-pick
                            if (f) onFile(f);
                        }}
                    />
                </div>
            </div>

            {paste && (
                <input
                    data-testid={`${testid}-url-input`}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://…"
                    className="w-full px-3 py-2 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none text-sm"
                />
            )}

            {samples?.length > 0 && (
                <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-gray mb-2">
                        Or pick from our gallery
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                        {samples.map((s) => (
                            <button
                                key={s}
                                type="button"
                                data-testid={`${testid}-sample-${s.slice(40, 60)}`}
                                onClick={() => onChange(s)}
                                className={`${aspect} rounded-md overflow-hidden border-2 transition-all ${
                                    value === s ? "border-ember-orange shadow" : "border-transparent hover:border-ridge-line"
                                }`}
                            >
                                <img src={s} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Multi-image uploader — used for adventure photo galleries.
 *
 * Props:
 *  - value: string[] of URLs.
 *  - onChange(urls): replacement array.
 *  - max: optional cap on total photos.
 */
export function MultiImageUploader({
    value = [],
    onChange,
    max = 20,
    testid = "photos",
    coverUrl,
    onSetCover,
}) {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
    const [lastResult, setLastResult] = useState(null); // {ok, fail, url}
    const [dragOver, setDragOver] = useState(false);
    // Pending thumbnails — one per file currently uploading. Each entry has
    // a local blob URL for the preview + an integer percentage so the grid
    // can show a real-time progress overlay.
    const [pending, setPending] = useState([]); // [{ id, name, previewUrl, pct }]
    const inputRef = useRef(null);
    // Refs let the window-level paste handler read latest state without
    // re-binding the listener on every value/uploading change.
    const valueRef = useRef(value);
    const uploadingRef = useRef(uploading);
    valueRef.current = value;
    uploadingRef.current = uploading;

    // Up to this many uploads in flight at once. R2 happily handles more,
    // but most home connections cap at ~3–4 concurrent uploads before
    // serialising; going higher just makes each one feel slower.
    const UPLOAD_CONCURRENCY = 4;

    const handleFiles = async (files) => {
        console.log("[upload] handleFiles entered with", files?.length || 0, "file(s)");
        const toUpload = Array.from(files).slice(0, Math.max(0, max - value.length));
        console.log("[upload] handleFiles toUpload:", toUpload.length, "max:", max, "value.length:", value.length);
        if (!toUpload.length) {
            toast.error(`Up to ${max} photos allowed.`);
            return;
        }
        setUploading(true);
        setLastResult(null);
        setProgress({ done: 0, total: toUpload.length, current: "" });

        // Validate + register pending thumbnails up-front so the user sees
        // every slot immediately, even before its turn in the pool comes up.
        const jobs = [];
        let skipped = 0;
        toUpload.forEach((file, i) => {
            if (!/^image\//.test(file.type)) {
                toast.error(`${file.name}: not an image — skipped.`);
                skipped++;
                return;
            }
            if (file.size > 25 * 1024 * 1024) {
                toast.error(`${file.name} is ${(file.size / 1024 / 1024).toFixed(1)}MB — max 25MB.`);
                skipped++;
                return;
            }
            const localId = `pending_${Date.now()}_${i}`;
            const previewUrl = URL.createObjectURL(file);
            jobs.push({ file, idx: jobs.length, localId, previewUrl });
        });
        // Single state write — avoids N renders for N drops.
        setPending(jobs.map((j) => ({ id: j.localId, name: j.file.name, previewUrl: j.previewUrl, pct: 0 })));
        console.log("[upload] handleFiles validated", jobs.length, "job(s), skipped", skipped, "starting worker pool");

        // Worker pool — UPLOAD_CONCURRENCY parallel `runJob`s pull from
        // the shared `jobs` queue (cursor index) until empty. Results
        // land in `results[idx]` so we can preserve input order when
        // appending to `value` at the end.
        const results = new Array(jobs.length);
        let cursor = 0;
        let done = 0;
        const total = jobs.length;
        const runJob = async () => {
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const myIdx = cursor++;
                if (myIdx >= jobs.length) return;
                const job = jobs[myIdx];
                try {
                    // Compression phase — overlay shows "Optimising…" via phase
                    setPending((prev) =>
                        prev.map((p) => (p.id === job.localId ? { ...p, phase: "compress" } : p))
                    );
                    const optimised = await maybeCompress(job.file);
                    setPending((prev) =>
                        prev.map((p) => (p.id === job.localId ? { ...p, phase: "upload", pct: 0 } : p))
                    );
                    const data = await uploadImageRaw(optimised, (pct) => {
                        setPending((prev) =>
                            prev.map((p) => (p.id === job.localId ? { ...p, pct } : p))
                        );
                    });
                    console.log("[upload]", job.file.name, "→", data);
                    results[job.idx] = data?.url
                        ? { ok: true, url: data.url }
                        : { ok: false, msg: "server returned no URL" };
                    if (!data?.url) {
                        toast.error(`${job.file.name}: server returned no URL`);
                    }
                } catch (err) {
                    console.error(`Upload failed for ${job.file.name}:`, err);
                    results[job.idx] = { ok: false, msg: err?.message || "Upload failed" };
                    toast.error(`${job.file.name}: ${err?.message || "Upload failed"}`, { duration: 10000 });
                } finally {
                    URL.revokeObjectURL(job.previewUrl);
                    setPending((prev) => prev.filter((p) => p.id !== job.localId));
                    done++;
                    setProgress({ done, total, current: "" });
                }
            }
        };
        await Promise.all(
            Array.from({ length: Math.min(UPLOAD_CONCURRENCY, jobs.length) }, runJob)
        );

        const urls = results.filter((r) => r?.ok).map((r) => r.url);
        const failed = results.filter((r) => r && !r.ok).length;
        skipped += failed;

        if (urls.length) {
            onChange([...value, ...urls]);
            toast.success(
                `✓ Uploaded ${urls.length} photo${urls.length === 1 ? "" : "s"}${skipped ? ` (${skipped} failed/skipped)` : ""}`,
                { duration: 5000 },
            );
        } else if (!skipped) {
            toast.error("No photos were uploaded — check the Console for details.", { duration: 10000 });
        }
        setLastResult({ ok: urls.length, fail: skipped });
        setProgress({ done: total, total, current: "" });
        setUploading(false);
    };

    // ----- paste-from-clipboard (Cmd-V / Ctrl-V) -----
    // Window-level listener so the user doesn't have to focus the dropzone
    // first. Ignored when an editable element (input/textarea/contenteditable)
    // is focused so we don't intercept normal text pastes.
    useEffect(() => {
        const onPaste = (e) => {
            if (uploadingRef.current) return;
            const target = e.target;
            if (target && (target.matches?.("input, textarea, [contenteditable=true]"))) return;
            const items = e.clipboardData?.items;
            if (!items) return;
            const files = [];
            for (const item of items) {
                if (item.kind === "file" && item.type?.startsWith("image/")) {
                    const f = item.getAsFile();
                    if (f) files.push(f);
                }
            }
            if (!files.length) return;
            if (valueRef.current.length >= max) {
                toast.error(`Up to ${max} photos allowed.`);
                return;
            }
            e.preventDefault();
            console.log("[upload] pasted", files.length, "image(s) from clipboard");
            handleFiles(files);
        };
        window.addEventListener("paste", onPaste);
        return () => window.removeEventListener("paste", onPaste);
        // handleFiles closes over state/setters but it's stable enough here —
        // we don't need to re-bind on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [max]);

    return (
        <div className="space-y-3">
            <div
                data-testid={`${testid}-dropzone`}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Only clear when leaving the dropzone, not its children.
                    if (e.currentTarget.contains(e.relatedTarget)) return;
                    setDragOver(false);
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOver(false);
                    const files = e.dataTransfer?.files;
                    console.log("[upload] dropped", files?.length || 0, "files");
                    if (files?.length && !uploading) handleFiles(files);
                }}
                className={`border-2 border-dashed rounded-md p-5 text-center transition-colors ${
                    dragOver
                        ? "border-ember-orange bg-ember-orange/5"
                        : "border-ridge-line bg-snowcap/50"
                }`}
            >
                <Upload className={`w-7 h-7 mx-auto mb-2 ${dragOver ? "text-ember-orange" : "text-slate-gray"}`} />
                <p className="text-sm font-semibold text-granite mb-1">
                    {dragOver ? "Drop to upload" : "Upload photos from your adventure"}
                </p>
                <p className="text-xs text-slate-gray mb-3">
                    Drag &amp; drop, paste (Ctrl/Cmd-V) or click — auto-optimised, up to 25MB each
                </p>
                <button
                    type="button"
                    data-testid={`${testid}-upload-btn`}
                    onClick={() => {
                        console.log("[upload] Choose-photos clicked, opening file picker");
                        inputRef.current?.click();
                    }}
                    disabled={uploading || value.length >= max}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold bg-ember-orange text-white hover:bg-ember-dark active:scale-95 disabled:opacity-50 transition-all"
                >
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {uploading ? "Uploading…" : value.length >= max ? `Max ${max} reached` : "Choose photos"}
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    data-testid={`${testid}-file-input`}
                    onChange={(e) => {
                        // Snapshot to a real Array BEFORE clearing the input.
                        // `e.target.files` is a *live* FileList — assigning
                        // `e.target.value = ""` empties it, so any later read
                        // through the original reference sees 0 files. This
                        // bug caused click-pick uploads to silently no-op
                        // while drag-and-drop (which uses dataTransfer.files,
                        // a separate FileList) kept working.
                        const files = Array.from(e.target.files || []);
                        console.log("[upload] file picker returned", files.length, "files");
                        e.target.value = "";
                        if (files.length) handleFiles(files);
                    }}
                />
                {uploading && progress.total > 0 && (
                    <div
                        data-testid={`${testid}-progress`}
                        className="mt-3 text-xs text-granite font-semibold"
                    >
                        Uploading {progress.done} of {progress.total}
                        {progress.done < progress.total ? "…" : " — finishing"}
                    </div>
                )}
                {!uploading && lastResult && (
                    <div
                        data-testid={`${testid}-result`}
                        className={`mt-3 text-xs font-bold inline-flex items-center gap-1 px-3 py-1.5 rounded-md ${
                            lastResult.ok > 0
                                ? "bg-deep-evergreen/10 text-deep-evergreen"
                                : "bg-red-50 text-red-700"
                        }`}
                    >
                        {lastResult.ok > 0
                            ? `✓ ${lastResult.ok} uploaded${lastResult.fail ? ` · ${lastResult.fail} failed` : ""}`
                            : `✗ ${lastResult.fail} failed — see Console`}
                    </div>
                )}
            </div>

            {(value.length > 0 || pending.length > 0) && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {value.map((url, i) => {
                        const isCover = !!onSetCover && coverUrl && coverUrl === url;
                        return (
                            <div
                                key={url + i}
                                className={`relative group aspect-square rounded-md overflow-hidden border ${
                                    isCover ? "border-ember-orange ring-2 ring-ember-orange/40" : "border-ridge-line"
                                }`}
                            >
                                <img
                                    src={resolveImageUrl(url)}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                                {/* Cover badge — always visible when this is the cover;
                                    hover-revealed "Set as cover" pill otherwise. */}
                                {onSetCover && (
                                    isCover ? (
                                        <span
                                            data-testid={`${testid}-cover-badge-${i}`}
                                            className="absolute top-1 left-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-ember-orange text-white text-[10px] font-bold tracking-wide shadow-sm"
                                        >
                                            <Star className="w-2.5 h-2.5 fill-white" /> COVER
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            data-testid={`${testid}-set-cover-${i}`}
                                            onClick={() => onSetCover(url)}
                                            className="absolute top-1 left-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-midnight-pine/85 hover:bg-ember-orange text-white text-[10px] font-bold tracking-wide opacity-0 group-hover:opacity-100 transition-all"
                                            aria-label="Set as cover photo"
                                        >
                                            <Star className="w-2.5 h-2.5" /> Set cover
                                        </button>
                                    )
                                )}
                                <button
                                    type="button"
                                    data-testid={`${testid}-remove-${i}`}
                                    onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-midnight-pine/90 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                    aria-label="Remove photo"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        );
                    })}
                    {/* Pending uploads — local-preview thumbnail with live % overlay */}
                    {pending.map((p) => (
                        <div
                            key={p.id}
                            data-testid={`${testid}-pending-${p.id}`}
                            className="relative aspect-square rounded-md overflow-hidden border border-ember-orange/40 bg-snowcap"
                        >
                            <img
                                src={p.previewUrl}
                                alt=""
                                className="w-full h-full object-cover opacity-60"
                            />
                            {/* Solid bottom bar grows left→right with upload % */}
                            <div className="absolute inset-x-0 bottom-0 h-1 bg-ember-orange/20">
                                <div
                                    className="h-full bg-ember-orange transition-[width] duration-200"
                                    style={{ width: `${p.pct}%` }}
                                />
                            </div>
                            {/* Centered percentage label OR "Optimising…" hint */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span
                                    data-testid={`${testid}-pending-pct-${p.id}`}
                                    className="text-sm font-extrabold text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] tabular-nums"
                                >
                                    {p.phase === "compress" ? "Optimising…" : `${p.pct}%`}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
